import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { IClient, IJob, ILeg } from "@/types";
import LoadAndRateInfo from "./LoadAndRateInfo";
import JobSummary from "./JobSummary";
import LegsSection from "./LegsSection";
import { generateLeg } from "@/utils/data-generators";
import { Select } from "@/shared";
import { useGetTemplatesQuery } from "@/redux/services/templates/api";
import {
  getTemplateContent,
  templateMatchesAppType,
} from "@/pages/Settings/Templates/template.utils";

interface LegsFormState {
  client?: IClient;
  appointmentDate: Date;
  form: any;
  legsData: any[];
  orderNumber: string;
  loadType: string;
  weight: number;
  driver: { fullName: string; _id: any };
  origin: any;
  destination: any;
  totalMiles: number;
  baseCharge: number;
  fuelSurcharge: number;
  dropCharge: number;
  otherCharges: number;
  totalCharges: number;
  calculatedTotalCharges?: number;
  useLegFlatRate: boolean;
  useCustomMileage: boolean;
  mileageSelectionType?: string;
  defaultMileage?: number;
  customMileage?: number | null;
  billedMiles?: number | null;
  useCustomBaseCharge: boolean;
  useCustomFuelSurcharge: boolean;
  useCustomDropCharge: boolean;
  useCustomOtherCharges: boolean;
  waitTime: boolean;
  totalWaitTime: number;
  waitStart: any;
  waitEnd: any;
  hasHandoff: boolean;
  isHandoff: boolean;
  [key: string]: any;
}

interface TruckingJobFormProps {
  legCreateLayout: boolean;
  form: {
    client: IClient;
    baseRate?: number;
    fuelRate?: number;
    useFlatRate?: boolean;
    [key: string]: any;
  };
  handleInputChange: (data: { id: string; value: any }) => void;
  handleDateChange: (date: Date | null) => void;
  handleRateChange: (id: string, value: number) => void;
  clientOriginLocation: any;
  updateLegs: (legs: ILeg[] | ((prevLegs: ILeg[]) => ILeg[])) => void;
  handleAddLeg: () => void;
  handleLegUpdate: (id: string, value: any) => void;
  renderKey: (index: number) => string;
  setFormInit: (initData: any, index: number) => void;
  prepareLegDataForLegCreate: (leg: ILeg, index: number) => ILeg;
  jobTotals: {
    totalMiles: number;
    totalWeight: number;
    totalBaseCharges: number;
    totalFuelSurcharges: number;
    totalDropCharges: number;
    totalCharges: number;
  };
  initialData?: {
    baseRate?: number;
    fuelRate?: number;
    flatRate?: number;
    useFlatRate?: boolean;
    [key: string]: any;
  };
  legs: ILeg[];
  setLegs: (legs: ILeg[] | ((prevLegs: ILeg[]) => ILeg[])) => void;
}

const TruckingJobForm: FC<TruckingJobFormProps> = ({
  legCreateLayout = false,
  form,
  handleInputChange,
  handleDateChange,
  handleRateChange,
  updateLegs,
  legs,
  setLegs,
  initialData,
}) => {
  const { data: legTemplateData, isLoading: legTemplatesLoading } =
    useGetTemplatesQuery({
      type: "leg",
      limit: 50,
    });

  const legTemplateOptions = useMemo(
    () =>
      (legTemplateData?.templates || [])
        .filter((template: any) => template.type === "leg")
        .filter((template: any) => templateMatchesAppType(template, "trucking"))
        .map((template: any) => ({
          label: template.name,
          value: template._id,
          template,
        })),
    [legTemplateData?.templates],
  );

  const normalizeMileageSelectionType = (value: any): string =>
    String(value || "")
      .toLowerCase()
      .trim();

  const isCustomMileageSelection = (value: any): boolean =>
    ["billedmileage", "billedmiles", "custominput", "currentmileage"].includes(
      normalizeMileageSelectionType(value),
    );

  /**
   * Resolve the effective mileage for calculations based on mileageSelectionType.
   * Create flow defaults to route/default mileage unless custom is explicitly selected.
   */
  const resolveLegMileageForCalculation = (
    leg: LegsFormState,
    explicitMiles?: number,
  ): number => {
    const routeDefaultMileage =
      leg?.route?.defaultMileage ?? leg?.route?.mileage ?? null;
    const defaultMileage =
      leg?.defaultMileage ?? routeDefaultMileage ?? leg?.totalMiles ?? 0;

    const customMileageCandidate =
      leg?.customMileage ??
      leg?.billedMiles ??
      leg?.route?.customMileage ??
      leg?.route?.billedMiles ??
      null;

    const hasExplicitCustomSelection = isCustomMileageSelection(
      leg?.mileageSelectionType,
    );
    const hasExplicitDefaultSelection =
      normalizeMileageSelectionType(leg?.mileageSelectionType) ===
      "defaultmileage";
    const shouldUseCustomMileage =
      hasExplicitCustomSelection || !!leg?.useCustomMileage;

    if (shouldUseCustomMileage) {
      return (
        Number(customMileageCandidate) ||
        Number(explicitMiles) ||
        Number(leg?.totalMiles) ||
        Number(defaultMileage) ||
        0
      );
    }

    if (hasExplicitDefaultSelection || !shouldUseCustomMileage) {
      return (
        Number(defaultMileage) ||
        Number(explicitMiles) ||
        Number(leg?.totalMiles) ||
        0
      );
    }

    return Number(explicitMiles) || Number(leg?.totalMiles) || Number(defaultMileage) || 0;
  };

  // Create initial form state for legs - handles both ILeg and parsed file data structures
  const createInitialFormState = useCallback(
    (leg?: any): LegsFormState => {
      // Exclude per-leg fields from initialData so job-level extracted values
      // (e.g. totalMiles: 0 at the job level) don't overwrite the per-leg
      // values calculated from the individual leg data below.
      const {
        totalMiles: _tm,
        defaultMileage: _dm,
        billedMiles: _bm,
        customMileage: _cm,
        route: _rt,
        origin: _og,
        destination: _ds,
        legs: _lg,
        ...safeInitialData
      } = initialData || {};

      const routeDefaultMileage =
        leg?.route?.defaultMileage ??
        leg?.route?.mileage ??
        leg?.route?.distance ??
        null;
      const fallbackTotalMiles = leg?.totalMiles ?? 0;
      const customMileageValue = leg?.customMileage ?? leg?.route?.customMileage ?? null;
      const billedMileageValue =
        leg?.billedMiles != null
          ? leg.billedMiles
          : leg?.route?.billedMiles != null
            ? leg.route.billedMiles
            : null;
      const explicitCustomSelection = isCustomMileageSelection(
        leg?.mileageSelectionType,
      );
      const shouldUseCustomMileage =
        explicitCustomSelection || leg?.useCustomMileage === true;
      const resolvedTotalMiles = shouldUseCustomMileage
        ? Number(customMileageValue ?? billedMileageValue ?? fallbackTotalMiles ?? routeDefaultMileage ?? 0)
        : Number(routeDefaultMileage ?? fallbackTotalMiles ?? 0);

      return {
        form: {},
        client: form.client,
        legsData: [],
        appointmentDate: leg?.appointmentDate
          ? new Date(leg.appointmentDate)
          : null,
        orderNumber: leg?.orderNumber || "",
        loadType: leg?.loadType || "Regular",
        weight: leg?.weight || leg?.totals?.totalOrderWeight || 0,
        driver: leg?.driver || { fullName: "", _id: null },
        // Handle both parsed file data (origin/destination directly) and ILeg structure (route.origin/destination)
        origin: leg?.origin || leg?.route?.origin || null,
        destination: leg?.destination || leg?.route?.destination || null,
        // Preserve any resolved route from extraction
        route: leg?.route || null,
        // Handle mileage from various possible sources.
        // defaultMileage is the user-set billing value and takes priority.
        // mileage is the computed distance from the provider.
        // totalMiles represents the "selected" mileage used for charges.
        totalMiles: resolvedTotalMiles,
        // Default mileage for the LegMileageSelector dropdown.
        // This is the "standard" route mileage before any custom override.
        defaultMileage:
          routeDefaultMileage || leg?.defaultMileage || fallbackTotalMiles || 0,
        // Only populate billedMiles when explicit data exists – do NOT
        // default to totalMiles, otherwise the dropdown thinks billed mileage
        // was set even though it wasn't (same value as default confuses the UI).
        billedMiles: billedMileageValue,
        customMileage: customMileageValue,
        baseCharge: leg?.baseCharge || 0,
        fuelSurcharge: leg?.fuelSurcharge || 0,
        dropCharge: leg?.dropCharge || 0,
        otherCharges: leg?.otherCharges || 0,
        totalCharges: leg?.totalCharges || leg?.calculatedTotalCharges || 0,
        calculatedTotalCharges:
          leg?.calculatedTotalCharges || leg?.totalCharges || 0,
        useLegFlatRate: leg?.useLegFlatRate || false,
        useCustomMileage: shouldUseCustomMileage,
        mileageSelectionType:
          leg?.mileageSelectionType ||
          (shouldUseCustomMileage ? "billedMileage" : "defaultMileage"),
        useCustomBaseCharge: leg?.useCustomBaseCharge || false,
        useCustomFuelSurcharge: leg?.useCustomFuelSurcharge || false,
        useCustomDropCharge: leg?.useCustomDropCharge || false,
        useCustomOtherCharges: leg?.useCustomOtherCharges || false,
        waitTime: Boolean(leg?.waitTime),
        totalWaitTime: leg?.waitTime?.total || 0,
        waitStart: leg?.waitTime?.start ? new Date(leg.waitTime.start) : null,
        waitEnd: leg?.waitTime?.end ? new Date(leg.waitTime.end) : null,
        hasHandoff: leg?.hasHandoff || false,
        isHandoff: leg?.isHandoff || false,
        // Spread safe job-level defaults (rates, flags) without overwriting per-leg data
        ...safeInitialData,
      };
    },
    [form.client, initialData],
  );

  // Legs state management - moved from LegsSection to parent
  const [legsFormState, setLegsFormState] = useState<LegsFormState[]>(() => [
    createInitialFormState(),
  ]);

  // Ref that always tracks the latest legsFormState so that delayed
  // timeouts (syncLegsToParent) never read stale closure data.
  const legsFormStateRef = useRef<LegsFormState[]>(legsFormState);
  legsFormStateRef.current = legsFormState; // updated every render

  // Single timeout ref for debounced sync – prevents stale timeouts
  // from overwriting correct data in the parent.
  const syncTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Cleanup sync timeout on unmount
  useEffect(() => {
    return () => {
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }
    };
  }, []);

  // Initialize legs form state when legs prop changes
  useEffect(() => {
    if (legs.length > 0) {
      const newLegsFormState = legs.map((leg: ILeg) =>
        createInitialFormState(leg),
      );

      setLegsFormState(newLegsFormState);
      // Update the ref immediately so any pending sync reads correct data
      legsFormStateRef.current = newLegsFormState;
    } else if (legs.length === 0 && legsFormState.length > 1) {
      // Reset to single empty leg when legs are cleared
      const resetState = [createInitialFormState()];
      setLegsFormState(resetState);
      legsFormStateRef.current = resetState;
    }
  }, [legs.length]); // Only depend on length to avoid infinite loops

  // Convert LegsFormState to ILeg - simplified to preserve actual form data
  const convertLegsFormStateToILeg = useCallback(
    (legsFormState: LegsFormState[]): any[] => {
      return legsFormState.map((legFormState, index) => {
        // New leg status should reflect admin inputs during job creation:
        // - appointment -> scheduled
        // - driver -> assigned
        // - else -> pending
        const appointmentDate = legFormState.appointmentDate || null;
        const driverId = legFormState.driver?._id || null;
        const status = appointmentDate
          ? "scheduled"
          : driverId
            ? "assigned"
            : "pending";
        const resolvedMileageForSave =
          resolveLegMileageForCalculation(legFormState);

        // Create a simple leg object that preserves the form data
        const convertedLeg = {
          // Basic leg info
          sortKey: index + 1,
          dropNumber: index + 1,
          orderNumber: legFormState.orderNumber || "",
          loadNumber: legFormState.orderNumber || "", // Use orderNumber as loadNumber fallback
          loadType: legFormState.loadType || "Regular",
          weight: legFormState.weight || 0,

          // Driver info
          driverId: legFormState.driver?._id || null,
          driver: legFormState.driver || null,

          // Location info
          origin: legFormState.origin?._id || legFormState.origin?.id || null,
          destination:
            legFormState.destination?._id ||
            legFormState.destination?.id ||
            null,

          // Route info
          route: {
            _id: legFormState.route?._id || null,
            origin:
              legFormState.route?.origin ||
              legFormState.origin?._id ||
              legFormState.origin?.id ||
              "",
            destination:
              legFormState.route?.destination ||
              legFormState.destination?._id ||
              legFormState.destination?.id ||
              "",
            mileage:
              legFormState.route?.mileage != null
                ? legFormState.route.mileage
                : legFormState.defaultMileage || resolvedMileageForSave || 0,
            defaultMileage:
              legFormState.defaultMileage ?? legFormState.route?.defaultMileage,
            customMileage:
              legFormState.customMileage ?? legFormState.route?.customMileage,
            billedMiles:
              legFormState.billedMiles ?? legFormState.route?.billedMiles,
            computedMilesExact: legFormState.route?.computedMilesExact,
            fullData: legFormState.route?.fullData,
          },

          // Financial data
          totalMiles: resolvedMileageForSave || 0,
          mileageUsed: resolvedMileageForSave || 0,
          mileageSelectionType:
            legFormState.mileageSelectionType ||
            (legFormState.useCustomMileage ? "billedMileage" : "defaultMileage"),
          baseCharge: legFormState.baseCharge || 0,
          fuelSurcharge: legFormState.fuelSurcharge || 0,
          dropCharge: legFormState.dropCharge || 0,
          otherCharges: legFormState.otherCharges || 0,
          totalCharges:
            legFormState.totalCharges ||
            legFormState.calculatedTotalCharges ||
            0,
          calculatedTotalCharges:
            legFormState.calculatedTotalCharges ||
            legFormState.totalCharges ||
            0,

          // Appointment info
          appointmentDate: legFormState.appointmentDate || null,
          appointmentTime: legFormState.appointmentTime || null,

          // Wait time info
          waitTime: {
            start: legFormState.waitStart || null,
            end: legFormState.waitEnd || null,
            total: legFormState.totalWaitTime || 0,
          },

          // Custom flags
          useCustomBaseCharge: legFormState.useCustomBaseCharge || false,
          useCustomFuelSurcharge: legFormState.useCustomFuelSurcharge || false,
          useCustomDropCharge: legFormState.useCustomDropCharge || false,
          useCustomOtherCharges: legFormState.useCustomOtherCharges || false,
          useLegFlatRate: legFormState.useLegFlatRate || false,

          // Status
          status,
          active: false,

          // Client info
          client: form.client?._id || null,
          parentCompany: form.client?.parentCompany || "",

          // Calculation metadata
          calculationMetadata: legFormState.calculationMetadata || null,

          // Customer info (for trucking)
          customerName: legFormState.customerName || "",
          customerPO: legFormState.customerPO || "",

          // Delivery address (for trucking)
          deliveryAddress: {
            line1: legFormState.deliveryAddress1 || "",
            line2: legFormState.deliveryAddress2 || "",
            city: legFormState.deliveryCity || "",
            state: legFormState.deliveryState || "",
            zip: legFormState.deliveryZip || "",
          },

          // Preserve any additional data
          ...legFormState,
        };

        return convertedLeg;
      });
    },
    [form],
  );

  /**
   * Debounced sync: always reads the LATEST legsFormState from the ref
   * inside the timeout, so stale closures from earlier effects can never
   * overwrite newer data.  Each call cancels the previous timeout,
   * guaranteeing only the most recent sync actually executes.
   */
  const syncLegsToParent = useCallback(
    (_unused?: any, source = "unknown") => {
      if (typeof setLegs !== "function") return;

      // Cancel any previously scheduled sync so stale data can't win
      if (syncTimeoutRef.current) {
        clearTimeout(syncTimeoutRef.current);
      }

      syncTimeoutRef.current = setTimeout(() => {
        syncTimeoutRef.current = null;

        // Always read the LATEST state from the ref – not from a closure
        const latestState = legsFormStateRef.current;

        const convertedLegs = convertLegsFormStateToILeg(latestState);

        setLegs(convertedLegs);
      }, 150); // single debounce window – no more nested timeouts
    },
    [setLegs, convertLegsFormStateToILeg],
  );

  // Recalculate totals when rate information changes

  // Create a stable reference for mileage tracking to prevent unnecessary recalculations
  const mileageHash = useMemo(() => {
    return legsFormState
      .map(
        (leg) =>
          `${resolveLegMileageForCalculation(leg) || 0}-${leg.defaultMileage || 0}-${leg.customMileage || 0}-${leg.billedMiles || 0}-${leg.mileageSelectionType || ""}-${leg.useCustomMileage ? 1 : 0}-${leg.baseCharge || 0}-${leg.fuelSurcharge || 0}`,
      )
      .join("|");
  }, [legsFormState]);

  // Recalculate totals when individual leg data changes (like mileage from route calculations)
  useEffect(() => {
    if (legsFormState.length > 0 && (form.baseRate || form.fuelRate)) {
      const hasLegsWithMileage = legsFormState.some(
        (leg) => resolveLegMileageForCalculation(leg) > 0,
      );

      if (hasLegsWithMileage) {
        const updatedLegsFormState = legsFormState.map((legFormState) => {
          const selectedMileage = resolveLegMileageForCalculation(legFormState);
          if (selectedMileage > 0) {
            const calculatedTotals = calculateLegTotals(
              legFormState,
              form,
              selectedMileage,
            );
            return { ...legFormState, ...calculatedTotals };
          }
          return legFormState;
        });

        // Only update if there are actual changes
        const hasChanges = updatedLegsFormState.some((updatedLeg, index) => {
          const originalLeg = legsFormState[index];
          if (!originalLeg || !updatedLeg) return false;

          return (
            Math.abs(
              (updatedLeg.totalCharges || 0) - (originalLeg.totalCharges || 0),
            ) > 0.01 ||
            Math.abs(
              (updatedLeg.baseCharge || 0) - (originalLeg.baseCharge || 0),
            ) > 0.01 ||
            Math.abs(
              (updatedLeg.fuelSurcharge || 0) -
                (originalLeg.fuelSurcharge || 0),
            ) > 0.01
          );
        });

        if (hasChanges) {
          // Update ref eagerly so debounced sync reads correct data
          legsFormStateRef.current = updatedLegsFormState;
          setLegsFormState(updatedLegsFormState);
          syncLegsToParent(null, "calculation-effect");
        }
      }
    }
  }, [
    form.baseRate,
    form.fuelRate,
    form.useFlatRate,
    mileageHash, // Use stable hash instead of creating new string each render
  ]);

  /**
   * Handle legs updates from LegsSection and schedule a debounced sync.
   * The ref is updated eagerly so that even if the React re-render hasn't
   * happened yet, any pending sync timeout will read the latest data.
   */
  const handleLegsFormStateUpdate = useCallback(
    (
      updatedLegsFormState:
        | LegsFormState[]
        | ((prevLegs: LegsFormState[]) => LegsFormState[]),
    ) => {
      if (typeof updatedLegsFormState === "function") {
        setLegsFormState((prevLegs) => {
          const newLegs = updatedLegsFormState(prevLegs);
          // Eagerly update the ref so the debounced sync reads correct data
          legsFormStateRef.current = newLegs;
          return newLegs;
        });
      } else {
        // Eagerly update the ref before React re-renders
        legsFormStateRef.current = updatedLegsFormState;
        setLegsFormState(updatedLegsFormState);
      }

      // Trigger a debounced sync – syncLegsToParent cancels previous
      // timeouts and always reads from legsFormStateRef, so stale
      // closures from earlier effects can never overwrite newer data.
      syncLegsToParent(null, "form-state-update");
    },
    [syncLegsToParent],
  );

  const handleAddLegTemplate = useCallback(
    (selected: any) => {
      const template = selected?.data?.template;
      const content = getTemplateContent(template);
      const defaults = content.defaults || content;

      if (!template || !defaults) return;

      const templateLeg = createInitialFormState(defaults);
      handleLegsFormStateUpdate((prevLegs) => [...prevLegs, templateLeg]);
    },
    [createInitialFormState, handleLegsFormStateUpdate],
  );

  const parseNumericValue = (value) => {
    if (typeof value === "number") return value;
    if (!value || value === "") return 0;
    const cleanValue = String(value).replace(/,/g, "");
    const numValue = parseFloat(cleanValue);
    return isNaN(numValue) ? 0 : numValue;
  };

  /**
   * Calculate hard-saved leg totals - core business logic
   * This replaces virtual field calculations with stored values
   */
  const calculateLegTotals = useCallback(
    (leg, jobForm, totalMiles = 0) => {
      const mileage = resolveLegMileageForCalculation(leg, totalMiles);
      const useFlatRate = jobForm.useFlatRate || false;
      const useLegFlatRate = leg.useLegFlatRate || false;

      // Get rate values and ensure they are numbers
      const baseRate = parseFloat(jobForm.baseRate) || 0;
      const fuelRate = parseFloat(jobForm.fuelRate) || 0;

      // Calculate individual charges
      let calculatedBaseCharge = 0;
      let calculatedFuelSurcharge = 0;
      let calculatedDropCharge = parseFloat(leg.dropCharge) || 0;
      let calculatedOtherCharges = parseFloat(leg.otherCharges) || 0;

      // Calculate drop charge based on load type if not using custom drop charge
      if (
        !useFlatRate &&
        !useLegFlatRate &&
        !leg.useCustomDropCharge &&
        leg.loadType
      ) {
        const loadTypeId = (leg.loadType || "regular").toLowerCase();
        const clientRates = jobForm.client?.rates || {};
        const loadTypeRate = clientRates[loadTypeId] || 0;

        if (loadTypeRate > 0) {
          calculatedDropCharge = parseFloat(loadTypeRate);
        }
      }

      if (!useFlatRate) {
        if (leg.useCustomBaseCharge) {
          calculatedBaseCharge = parseFloat(leg.baseCharge) || 0;
        } else if (useLegFlatRate) {
          calculatedBaseCharge = parseFloat(leg.baseCharge) || 0;
        } else {
          calculatedBaseCharge = parseNumericValue(mileage * baseRate);
        }

        if (useLegFlatRate) {
          calculatedFuelSurcharge = 0;
        } else if (leg.useCustomFuelSurcharge) {
          calculatedFuelSurcharge = parseFloat(leg.fuelSurcharge) || 0;
        } else {
          calculatedFuelSurcharge = parseNumericValue(mileage * fuelRate);
        }
      }

      // Calculate total charges
      let calculatedTotalCharges;
      if (useFlatRate || useLegFlatRate) {
        calculatedTotalCharges = calculatedBaseCharge + calculatedOtherCharges;
      } else {
        calculatedTotalCharges =
          calculatedBaseCharge +
          calculatedFuelSurcharge +
          calculatedDropCharge +
          calculatedOtherCharges;
      }

      const result = {
        baseCharge: calculatedBaseCharge,
        fuelSurcharge: calculatedFuelSurcharge,
        dropCharge: calculatedDropCharge,
        otherCharges: calculatedOtherCharges,
        calculatedTotalCharges,
        totalCharges: calculatedTotalCharges, // For backwards compatibility
        calculationMetadata: {
          mileageUsed: mileage,
          ratesUsed: { baseRate, fuelRate },
          flagsUsed: { useFlatRate, useLegFlatRate },
          calculatedAt: new Date().toISOString(),
        },
      };

      return result;
    },
    [form.client, form.baseRate, form.fuelRate, form.useFlatRate],
  );

  /**
   * Apply calculated totals to leg data for persistence
   */
  const applyCalculatedTotalsToLeg = useCallback(
    (leg, legIndex, jobForm) => {
      const selectedMileage = resolveLegMileageForCalculation(leg, leg.totalMiles);
      const calculatedTotals = calculateLegTotals(leg, jobForm, selectedMileage);

      return {
        ...leg,
        ...calculatedTotals,
      };
    },
    [calculateLegTotals],
  );

  /**
   * Calculate job-level totals by aggregating all legs
   */
  // const calculateJobTotals = useCallback(
  //   (legs, jobForm) => {
  //     if (!Array.isArray(legs) || legs.length === 0) {
  //       return {
  //         totalMiles: 0,
  //         totalWeight: 0,
  //         totalBaseCharges: 0,
  //         totalFuelSurcharges: 0,
  //         totalDropCharges: 0,
  //         totalOtherCharges: 0,
  //         totalCharges: 0,
  //         legCount: 0,
  //       };
  //     }

  //     return legs.reduce(
  //       (totals, leg) => {
  //         // Apply current calculations to get up-to-date values
  //         const calculatedLeg = calculateLegTotals(
  //           leg,
  //           jobForm,
  //           leg.totalMiles
  //         );

  //         return {
  //           totalMiles:
  //             totals.totalMiles +
  //             parseNumericValue(leg.totalMiles || leg.defaultMileage || 0),
  //           totalWeight:
  //             totals.totalWeight +
  //             parseNumericValue(
  //             ),
  //           totalBaseCharges:
  //             totals.totalBaseCharges +
  //             parseNumericValue(calculatedLeg.baseCharge || 0),
  //           totalFuelSurcharges:
  //             totals.totalFuelSurcharges +
  //             parseNumericValue(calculatedLeg.fuelSurcharge || 0),
  //           totalDropCharges:
  //             totals.totalDropCharges +
  //             parseNumericValue(calculatedLeg.dropCharge || 0),
  //           totalOtherCharges:
  //             totals.totalOtherCharges +
  //             parseNumericValue(calculatedLeg.otherCharges || 0),
  //           totalCharges:
  //             totals.totalCharges +
  //             parseNumericValue(calculatedLeg.calculatedTotalCharges || 0),
  //           legCount: totals.legCount + 1,
  //         };
  //       },
  //       {
  //         totalMiles: 0,
  //         totalWeight: 0,
  //         totalBaseCharges: 0,
  //         totalFuelSurcharges: 0,
  //         totalDropCharges: 0,
  //         totalOtherCharges: 0,
  //         totalCharges: 0,
  //         legCount: 0,
  //       }
  //     );
  //   },
  //   [calculateLegTotals]
  // );

  // // Handle legs update from LegsSection
  // const handleLegsUpdate = useCallback(
  //   (updatedLegsFormState: LegsFormState[]) => {
  //     // Update the local legs form state
  //     setLegsFormState(updatedLegsFormState);

  //     if (typeof updateLegs === "function") {
  //       // Add a small delay to ensure route calculations are complete
  //       setTimeout(() => {
  //         const updatedLegs = convertLegsFormStateToILeg(updatedLegsFormState);
  //         updateLegs(updatedLegs);
  //       }, 100);
  //     }
  //   },
  //   [convertLegsFormStateToILeg, updateLegs]
  // );

  // Calculate job totals using the legsFormState
  const calculatedJobTotals = useMemo(() => {
    const defaultTotals = {
      totalMiles: 0,
      totalWeight: 0,
      totalBaseCharges: 0,
      totalFuelSurcharges: 0,
      totalDropCharges: 0,
      totalOtherCharges: 0,
      totalCharges: 0,
      legCount: 0,
    };

    // Safety check for legs array
    if (!Array.isArray(legsFormState) || legsFormState.length === 0) {
      return defaultTotals;
    }

    // Filter out any undefined legs
    const validLegs = legsFormState.filter(
      (leg) => leg !== undefined && leg !== null,
    );

    // If no valid legs, return default totals
    if (validLegs.length === 0) {
      return defaultTotals;
    }

    // Calculate totals for all legs, even if some don't have miles yet
    // This prevents showing 0 when legs are still being calculated
    return validLegs.reduce((totals, leg) => {
      if (!leg) return totals;
      const selectedMileage = resolveLegMileageForCalculation(leg);

      // Always calculate leg totals to ensure we use the most up-to-date values
      const calculatedLeg = calculateLegTotals(leg, form, selectedMileage || 0);

      return {
        totalMiles: totals.totalMiles + (selectedMileage || 0),
        totalWeight: totals.totalWeight + (parseNumericValue(leg.weight) || 0),
        // Use calculatedLeg values instead of raw leg values to ensure consistency
        totalBaseCharges:
          totals.totalBaseCharges + (calculatedLeg.baseCharge || 0),
        totalFuelSurcharges:
          totals.totalFuelSurcharges + (calculatedLeg.fuelSurcharge || 0),
        totalDropCharges:
          totals.totalDropCharges + (calculatedLeg.dropCharge || 0),
        totalOtherCharges:
          totals.totalOtherCharges + (calculatedLeg.otherCharges || 0),
        // Use calculatedTotalCharges for consistency
        totalCharges:
          totals.totalCharges +
          (calculatedLeg.calculatedTotalCharges ||
            calculatedLeg.totalCharges ||
            0),
        legCount: totals.legCount + 1,
      };
    }, defaultTotals);
  }, [
    legsFormState,
    form.baseRate,
    form.fuelRate,
    form.useFlatRate,
    form.client, // Add form.client for rate calculations
  ]);

  // Initialize legs from initialData if available
  useEffect(
    () => {
      // if (initialData?.legs && typeof setEditableLegs === "function") {
      //   const processedLegs = initialData.legs.map((leg, index) => {
      //     const preparedLeg = prepareLegDataForLegCreate(leg, index);
      //     return applyCalculatedTotalsToLeg(preparedLeg, index, form);
      //   });
      //   setEditableLegs(processedLegs);
      // }
    },
    [
      // initialData,
      // prepareLegDataForLegCreate,
      // applyCalculatedTotalsToLeg,
      // setEditableLegs,
    ],
  );

  return (
    <div className="space-y-4">
      <LoadAndRateInfo
        form={form}
        handleInputChange={handleInputChange}
        handleDateChange={handleDateChange}
        handleRateChange={handleRateChange}
      />
      <JobSummary
        jobTotals={
          calculatedJobTotals || {
            totalMiles: 0,
            totalWeight: 0,
            totalBaseCharges: 0,
            totalFuelSurcharges: 0,
            totalDropCharges: 0,
            totalCharges: 0,
            legCount: 0,
          }
        }
        useFlatRate={form.useFlatRate}
      />
      {legTemplateOptions.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-3">
          <Select
            label="Add Leg From Template"
            placeholder="Select a leg template"
            value=""
            loading={legTemplatesLoading}
            options={legTemplateOptions}
            onChange={handleAddLegTemplate}
            size="sm"
          />
        </div>
      )}
      <LegsSection
        form={form}
        client={form.client}
        legsData={legsFormState}
        onLegsUpdate={handleLegsFormStateUpdate}
        initialData={{
          baseRate: form.baseRate,
          fuelRate: form.fuelRate,
          flatRate: form.flatRate,
          useFlatRate: form.useFlatRate,
        }}
        legCreateLayout={legCreateLayout}
      />
    </div>
  );
};

export default TruckingJobForm;
